'use client';

import { lastminuteDealsGradientOnButton } from '@/app/common/components/Promotions/styled';
/**
 * Smoothly scroll to `#id`s.
 */
import { Button, Link, type LinkVariant } from '@lmn/design-system';
import * as React from 'react';
import styled from 'styled-components';

type interactionCallbackFactoryProps = {
  sectionId: string;
  center?: boolean;
  offset?: number;
  onClick: ((event: React.SyntheticEvent) => void) | undefined;
  scrollToTop?: boolean;
  timeout?: number;
};

function interactionCallbackFactory(props: interactionCallbackFactoryProps) {
  const { sectionId, onClick, center = false, scrollToTop = false, offset = 0, timeout = 0 } = props;

  return (event: React.SyntheticEvent) => {
    event.preventDefault();

    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    onClick && onClick(event);
    setTimeout(() => {
      const element = document.querySelector(`#${sectionId}`) as HTMLElement;
      if (element || scrollToTop) {
        window.scrollTo({
          top: scrollToTop
            ? 0
            : center
              ? element.offsetTop - window.innerHeight / 2 + offset
              : element.offsetTop - offset,
          behavior: 'smooth',
        });
      }
    }, timeout);
  };
}

type ScrollToIdLinkProps = {
  /** `id` of the DOM element to scroll to. */
  targetId: string;
  variant?: LinkVariant;
  center?: boolean;
  offset?: number;
  onClick?: (event: React.SyntheticEvent) => void;
} & React.HTMLAttributes<HTMLAnchorElement>;

/**
 * Anchor link (`<a href... />`) that smoothly scrolls to a DOM element whos `id` resembles the `targetId` from props.
 * Uses regular `href` functionality before rehydrating.
 * For a nicer visual experience the target element is not aligned directly at the top but shifted a little bit down.
 *
 */
export default function ScrollToIdLink(props: ScrollToIdLinkProps) {
  const { children, role, targetId, center = false, offset = 0, onClick, variant = 'accent', ...rest } = props;
  const interactionCallback = interactionCallbackFactory({ sectionId: targetId, center, offset, onClick });
  return (
    <Link
      {...rest}
      as="a"
      variant={variant}
      role={role}
      href={`#${targetId}`}
      onKeyDown={(event) => (event.key === 'Enter' ? interactionCallback(event) : null)}
      onClick={interactionCallback}
    >
      {children}
    </Link>
  );
}

type ScrollToIdButtonProps = {
  /** `id` of the DOM element to scroll to. */
  targetId: string;
  center?: boolean;
  offset?: number;
  onClick?: (event: React.SyntheticEvent) => void;
  timeout?: number;
  isLmnDeal?: boolean;
} & React.ComponentProps<typeof Button>;

const StyledButton = styled(Button)<{ $isLmnDeal?: boolean }>`
  ${(p) => p.$isLmnDeal && lastminuteDealsGradientOnButton};
`;

/**
 * Button that smoothly scrolls to a DOM element whos `id` resembles the `targetId` from props.
 * Uses regular `href` functionality before rehydrating.
 * For a nicer visual experience the target element is not aligned directly at the top but shifted a little bit down.
 */
export function ScrollToIdButton(props: ScrollToIdButtonProps) {
  const { children, role, targetId, center, offset, onClick, timeout, isLmnDeal, ...rest } = props;
  const interactionCallback = interactionCallbackFactory({ sectionId: targetId, offset, center, timeout, onClick });
  return (
    <StyledButton
      {...rest}
      $isLmnDeal={isLmnDeal}
      role={role}
      onKeyPress={interactionCallback}
      onClick={interactionCallback}
    >
      {children}
    </StyledButton>
  );
}
